import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from './utils';

/**
 * Button style variants powered by class-variance-authority.
 * Exported so consumers can compose the same look on non-button elements
 * (e.g. anchors, menu items, or Radix triggers).
 */
export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg',
    'text-sm font-medium',
    'transition-all duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 active:bg-primary/95',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 active:bg-destructive/95',
        outline:
          'border border-border bg-card text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost:
          'text-foreground hover:bg-accent hover:text-accent-foreground',
        link:
          'text-primary underline-offset-4 hover:underline focus-visible:ring-offset-0',
        success:
          'bg-emerald-500 text-white shadow-sm hover:bg-emerald-600 active:bg-emerald-700',
        warning:
          'bg-amber-500 text-white shadow-sm hover:bg-amber-600 active:bg-amber-700',
        /* -----------------------------------------------------------------
         * Semantic soft-outline variants (Phase E7).
         * Every one keeps the outline shape but tints border + icon + label
         * with its semantic color so the action is instantly identifiable
         * on a busy toolbar.  Prefer these over hard-coded `text-*-600`
         * classes on <Icon /> children — the icon inherits currentColor
         * from the button and lands in the right tone automatically.
         * ----------------------------------------------------------------- */
        positive:
          'border border-emerald-300 bg-emerald-50 text-emerald-700 shadow-sm hover:bg-emerald-100 hover:text-emerald-800 dark:border-emerald-500/50 dark:bg-emerald-500/10 dark:text-emerald-300 dark:hover:bg-emerald-500/20',
        info:
          'border border-sky-300 bg-sky-50 text-sky-700 shadow-sm hover:bg-sky-100 hover:text-sky-800 dark:border-sky-500/50 dark:bg-sky-500/10 dark:text-sky-300 dark:hover:bg-sky-500/20',
        softWarning:
          'border border-amber-300 bg-amber-50 text-amber-800 shadow-sm hover:bg-amber-100 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20',
        softDestructive:
          'border border-rose-300 bg-rose-50 text-rose-700 shadow-sm hover:bg-rose-100 hover:text-rose-800 dark:border-rose-500/50 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20',
      },
      size: {
        sm: 'h-7 px-3 text-xs [&_svg]:size-3.5',
        default: 'h-8 px-3 text-sm',
        lg: 'h-9 px-6 text-sm',
        xl: 'h-12 px-8 text-base [&_svg]:size-5',
        icon: 'h-8 w-8 p-0',
        'icon-sm': 'h-7 w-7 p-0 [&_svg]:size-3.5',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fullWidth: false,
    },
  },
);

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>,
    VariantProps<typeof buttonVariants> {
  /** Render as the immediate child element (Radix Slot pattern). */
  asChild?: boolean;
  /** Icon rendered before children. */
  leftIcon?: ReactNode;
  /** Icon rendered after children. */
  rightIcon?: ReactNode;
  /** When true, replaces the left icon with a spinner and disables the button. */
  loading?: boolean;
  /** Native disabled — accepted normally, we omit + re-add it so `loading` can force it. */
  disabled?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      asChild = false,
      leftIcon,
      rightIcon,
      loading = false,
      disabled,
      type,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;

    // When used with `asChild`, Radix Slot requires a single child element,
    // so we merge our decorations into that child rather than adding siblings.
    const content = (
      <>
        {loading ? <Loader2 className="animate-spin" aria-hidden /> : leftIcon}
        {children}
        {!loading && rightIcon}
      </>
    );

    if (asChild) {
      const child = Children.only(children);
      if (!isValidElement(child)) {
        // Fall through to regular slot behaviour — Radix will throw a helpful error.
        return (
          <Comp
            ref={ref}
            className={cn(buttonVariants({ variant, size, fullWidth, className }))}
            data-loading={loading ? '' : undefined}
            aria-disabled={isDisabled || undefined}
            {...props}
          >
            {children}
          </Comp>
        );
      }
      const childEl = child as ReactElement<{ children?: ReactNode }>;
      return (
        <Comp
          ref={ref}
          className={cn(buttonVariants({ variant, size, fullWidth, className }))}
          data-loading={loading ? '' : undefined}
          aria-disabled={isDisabled || undefined}
          {...props}
        >
          {cloneElement(childEl, {
            children: (
              <>
                {loading ? <Loader2 className="animate-spin" aria-hidden /> : leftIcon}
                {childEl.props.children}
                {!loading && rightIcon}
              </>
            ),
          })}
        </Comp>
      );
    }

    return (
      <button
        ref={ref}
        type={type ?? 'button'}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        data-loading={loading ? '' : undefined}
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        {...props}
      >
        {content}
      </button>
    );
  },
);
Button.displayName = 'Button';

export interface ButtonGroupProps extends HTMLAttributes<HTMLDivElement> {
  /** Stack children vertically instead of horizontally. */
  orientation?: 'horizontal' | 'vertical';
}

/**
 * Visually joins adjacent Buttons: the first keeps its left radius, the last
 * keeps its right radius, and everything in between is squared off with a
 * shared border seam.
 */
export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(
  ({ className, orientation = 'horizontal', role = 'group', ...props }, ref) => (
    <div
      ref={ref}
      role={role}
      data-orientation={orientation}
      className={cn(
        'inline-flex isolate',
        orientation === 'horizontal'
          ? [
              'flex-row',
              '[&>*]:rounded-none',
              '[&>*:first-child]:rounded-l-lg',
              '[&>*:last-child]:rounded-r-lg',
              '[&>*:not(:first-child)]:-ml-px',
              '[&>*:focus-visible]:relative [&>*:focus-visible]:z-10',
              '[&>*:hover]:relative [&>*:hover]:z-10',
            ].join(' ')
          : [
              'flex-col',
              '[&>*]:rounded-none',
              '[&>*:first-child]:rounded-t-lg',
              '[&>*:last-child]:rounded-b-lg',
              '[&>*:not(:first-child)]:-mt-px',
              '[&>*:focus-visible]:relative [&>*:focus-visible]:z-10',
              '[&>*:hover]:relative [&>*:hover]:z-10',
            ].join(' '),
        className,
      )}
      {...props}
    />
  ),
);
ButtonGroup.displayName = 'ButtonGroup';
